/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { ZodError } from 'zod';

import { isCustomJsonResponse } from '@fai/third-party/react-router-dom';

import { ApolloErrorPage } from './api-error-pages';
import ErrorPage from './error-page';
import { CustomJsonErrorPage, ErrorResponsePage, ValidationErrorPage } from './runtime-error-pages';

const patternMatchError = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    if (isCustomJsonResponse(error)) {
      return <CustomJsonErrorPage error={error} />;
    }

    return <ErrorResponsePage error={error} />;
  }

  if (error instanceof ZodError) {
    return <ValidationErrorPage error={error} />;
  }

  if (error instanceof ApolloError) {
    return <ApolloErrorPage error={error} />;
  }

  return false;
};

export function RootErrorBoundary() {
  const { t } = useTranslation();
  const error = useRouteError();
  const filteredError = patternMatchError(error);

  /* Uncaught Error! */
  if (!filteredError) {
    return (
      <ErrorPage
        message={t('error.we_track_these_error')}
        title={t('error.looks_like_something_wrong')}
      />
    );
  }

  return filteredError;
}
